import React, { useState } from "react";
import { Button } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import UpgradeModal from "@iso/containers/Pages/Property/SubscriptionModal";
import {
  BILLING_PERIOD,
  ONBOARDING_PLAN,
  PAID_PLAN_PRICES,
  formatPrice,
} from "@iso/constants/onboarding";

// Features listed are what each plan unlocks in the app today
// (Layout/Sidebar/hiddenKeys.js for the paid pages + the Silver/Gold checks
// in PropertyMap.js).
// Paid plans take their price and Stripe price id from PAID_PLAN_PRICES.
const PLANS = [
  {
    key: ONBOARDING_PLAN.BRONZE,
    eyebrow: "Free version",
    name: "Bronze",
    features: [
      "Unlimited users and property size",
      "Property map (safety asset locations & traffic management)",
      "Task management",
      "Hazard management",
      "Asset management",
      "Service logs, defect management & lock out tag out",
    ],
  },
  {
    key: ONBOARDING_PLAN.SILVER,
    eyebrow: "More of your property",
    name: "Silver",
    features: [
      "Everything in Bronze",
      "Weather module",
      "Satellite imagery",
      "One custom map layer",
    ],
  },
  {
    key: ONBOARDING_PLAN.GOLD,
    eyebrow: "Our top subscription",
    name: "Gold",
    features: [
      "Everything in Silver",
      "Health & Safety and Safety Documents",
      "Incident module",
      "Sensors",
      "SafeFarm (Loneworker)",
      "Spray",
      "Activity",
      "Unlimited custom map layers",
    ],
  },
];

const BILLING_OPTIONS = [
  { value: BILLING_PERIOD.MONTHLY, label: "Monthly" },
  { value: BILLING_PERIOD.ANNUAL, label: "Annual" },
];

// What a paid plan costs for the chosen billing period, and what paying
// annually saves over twelve monthly payments (shown as the real figure — the
// percentage differs a lot between plans).
const priceFor = (planKey, billing) => {
  const prices = PAID_PLAN_PRICES[planKey];
  if (!prices) return null;
  const { amount, stripePlan } = prices[billing];
  const saving =
    billing === BILLING_PERIOD.ANNUAL
      ? prices[BILLING_PERIOD.MONTHLY].amount * 12 - amount
      : 0;
  return {
    label: formatPrice(amount),
    note: billing === BILLING_PERIOD.ANNUAL ? "/yr" : "/mo",
    saving: saving > 0 ? `Save ${formatPrice(saving)} a year` : null,
    stripePlan,
  };
};

// Bronze carries on for free (the final step then offers the Gold trial);
// Silver and Gold open the existing paid-upgrade form for the new property.
const PlanStep = ({ propertyId, plan, onChosen }) => {
  const [selected, setSelected] = useState(plan || ONBOARDING_PLAN.BRONZE);
  const [billing, setBilling] = useState(BILLING_PERIOD.MONTHLY);
  const [visibleUpgradeModal, setVisibleUpgradeModal] = useState(false);
  const selectedPrice = priceFor(selected, billing);
  // A paid plan bought here (then Back) locks the step: the upgrade form would
  // open a second subscription rather than change the first. Plans are changed
  // from the Properties page, which updates the existing subscription.
  // While locked the step names no plan, price or billing period: the upgrade
  // form lets the buyer change both, so the card clicked here may not be what
  // was bought.
  const locked = Boolean(PAID_PLAN_PRICES[plan]);

  const choose = (key) => setSelected(key);

  const handleContinue = () => {
    if (selectedPrice && !locked) {
      setVisibleUpgradeModal(true);
    } else {
      onChosen(locked ? plan : selected);
    }
  };

  return (
    <>
      <div className="ob-plan-header">
        <h1 className="ob-title" tabIndex={-1}>
          Choose your plan
        </h1>
        {!locked && (
          <div className="ob-billing" role="radiogroup" aria-label="Billing period">
            {BILLING_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={billing === option.value}
                className={`ob-billing-option ${billing === option.value ? "is-active" : ""}`}
                onClick={() => setBilling(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div
        className={`ob-plans ${locked ? "is-locked" : ""}`}
        role={locked ? undefined : "radiogroup"}
        aria-label={locked ? undefined : "Plan"}
      >
        {PLANS.map((option) => {
          const isSelected = !locked && selected === option.key;
          const price = priceFor(option.key, billing);
          return (
            <div
              key={option.key}
              className={`ob-plan ${isSelected ? "is-selected" : ""}`}
              onClick={locked ? undefined : () => choose(option.key)}
            >
              <span className="ob-plan-eyebrow">{option.eyebrow}</span>
              <h2 className="ob-plan-name">{option.name}</h2>
              {!locked && (
                <div className="ob-plan-price">
                  {price ? price.label : "FREE"}
                  {price && <small>{price.note}</small>}
                </div>
              )}
              {!locked && price && price.saving && (
                <div className="ob-plan-saving">{price.saving}</div>
              )}
              <ul className="ob-plan-features">
                {option.features.map((feature) => (
                  <li key={feature}>
                    <CheckOutlined />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {!locked && (
                <Button
                  className="ob-plan-select"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => choose(option.key)}
                >
                  {isSelected ? "Selected" : `Choose ${option.name}`}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <p className="ob-note">
        {locked &&
          "Your property is on a paid plan. You can change it from the Properties page. "}
        If you'd like us to help with an upgrade, email{" "}
        <a href="mailto:support@airagri.com.au">support@airagri.com.au</a>.
      </p>
      <div className="ob-actions">
        <Button className="ob-btn-primary" onClick={handleContinue}>
          Continue
        </Button>
      </div>
      {visibleUpgradeModal && (
        <UpgradeModal
          visible
          property={{
            id: propertyId,
            subscription_id: null,
            subscription_label: selectedPrice.stripePlan,
          }}
          cancelModal={() => setVisibleUpgradeModal(false)}
          onSaved={() => onChosen(selected)}
        />
      )}
    </>
  );
};

export default PlanStep;
